import { loadPublicStats, PUBLIC_STATS_SNAPSHOT } from 'pb-public-stats';
import type { Company } from '@/types/person/companies';
import { COMPANY_TYPE_LABELS, CompanyTypeEnum } from '@/types/person/companies';
import { companiesService } from '~/services/companies-service';
import { publicService } from '~/services/public-service';
import type { BaseAPIResponse } from '~/types/api';
import { ApiError } from '~/utils/errors';

/**
 * Catalog filter labels for one set of coverage counts. The count rides in the label
 * because the filters read as "People Search (360)".
 */
const categoryLabels = (brokerTypes: Record<string, number>): Record<string, string> =>
    Object.entries(brokerTypes).reduce((acc, [key, count]) => {
        const normalizedKey = key === 'list_brokers' ? 'data_broker' : key;
        const label = COMPANY_TYPE_LABELS[normalizedKey as CompanyTypeEnum];
        if (label) {
            acc[normalizedKey] = `${label} (${count})`;
        }
        return acc;
    }, {} as Record<string, string>);

export const useCompaniesStore = defineStore(
    'companies',
    () => {
        const { showError } = useGlobalToast();
        const selectedCompany = ref<Company | null>(null);
        const companies = ref<Company[]>([]);
        const companyCacheTimestamps = ref<Record<number, number>>({});
        const route = useRoute();
        const companyResponse = ref<BaseAPIResponse<Company[]>>();
        const isLoading = ref(false);
        const publicFetchFailed = ref(false);
        // Seeded from the committed snapshot, never from zero: the broker catalog is a public
        // page and its first paint states a coverage figure, so an empty start renders
        // "All Data Brokers (0)" for as long as the live answer takes — or forever, if it
        // never comes. fetchCategoryStats replaces these with the live counts.
        const categories = ref<Record<string, string>>(categoryLabels(PUBLIC_STATS_SNAPSHOT.stats.data_broker_types));
        // Copied, not aliased: the snapshot object is shared by every consumer in the process.
        const brokerCategories = ref<Record<string, number>>({ ...PUBLIC_STATS_SNAPSHOT.stats.data_broker_types });
        const totalDataBrokers = ref(PUBLIC_STATS_SNAPSHOT.stats.data_brokers);
        const categoryStatsFetched = ref(false);
        // Resolved here, in the store body, where a Nuxt instance is guaranteed — never inside
        // an action, which can run a tick after the context that owns the runtime config.
        const statsEndpoint = hivePublicStatsEndpoint();

        // Cache expiration time (5 minutes)
        const CACHE_EXPIRY = 5 * 60 * 1000;

        // Whether a record fetched at `fetchedAt` is still fresh
        const isCacheValid = (fetchedAt: number) => {
            return Date.now() - fetchedAt < CACHE_EXPIRY;
        };

        // The store's record of a company, stamped with when it was fetched. A record fetched again
        // replaces the one held: it is the fresher answer, and the stamp says so.
        const addCompanyToCache = (company: Company) => {
            companyCacheTimestamps.value[company.id] = Date.now();

            const held = companies.value.findIndex((c) => c.id === company.id);
            if (held === -1) {
                companies.value.push(company);
            } else {
                companies.value[held] = company;
            }
        };

        // The fetch in flight for each record. Every card that renders a stale or missing record asks
        // for it, so the readers that ask while one runs wait on that one.
        const fetchesInFlight = new Map<number, Promise<void>>();

        const searchQuery = ref('');

        // Computed property to get company by ID, fetching if not exists
        const getCompanyById = computed(() => {
            return (companyId: number) => {
                // Check if company exists and cache is valid
                const company = companies.value.find((c: Company) => c.id === companyId);

                if (company) {
                    const timestamp = companyCacheTimestamps.value[companyId];
                    if (!timestamp || !isCacheValid(timestamp)) {
                        void fetchCompany(companyId);
                    }

                    return company;
                }

                // If company not found, fetch it asynchronously
                void fetchCompany(companyId);

                // Return undefined initially, will update when fetch completes
                return undefined;
            };
        });

        // Computed property for filtered companies based on search query
        const filteredCompanies = computed(() => {
            let filtered = companies.value;

            if (searchQuery.value) {
                filtered = filtered.filter((company) => company.name.toLowerCase().includes(searchQuery.value.toLowerCase()));
            }

            // Sort by broker type: people_search first, people_search_affiliate second, then others
            return filtered.sort((a, b) => {
                const getPriority = (company: Company) => {
                    if (company.type.value === 'people_search') return 1;
                    if (company.type.value === 'people_search_affiliate') return 2;
                    return 3;
                };

                return getPriority(a) - getPriority(b);
            });
        });

        // Getter for category keys used in searching
        const categoryKeys = computed(() => {
            return Object.values(CompanyTypeEnum);
        });

        const selectedCategoryType = ref<string[]>([]);

        function resetCompanies() {
            companies.value = [];
            companyCacheTimestamps.value = {};
            companyResponse.value = undefined;
        }

        async function fetchPublicCompanies(
            query: string = '',
            per_page: number = 50,
            page: number = 1,
            type: Array<string> = [],
            license?: string
        ) {
            if (query && query.length < 3) return;

            isLoading.value = true;
            publicFetchFailed.value = false;
            searchQuery.value = query;
            selectedCategoryType.value = type;

            try {
                const data = await publicService.searchCompaniesV3(query, per_page, page, type.filter((t) => t.trim()).join(','), license);
                if (page === 1) {
                    // Clear existing companies and add new ones with timestamps
                    companies.value = [];
                    data.data.forEach((company) => addCompanyToCache(company));

                    // Only select company based on URL parameter on first page load
                    const companyName = route.query.company;
                    if (companyName) {
                        const company = companies.value.find((c: Company) => c.name === companyName);
                        if (company) {
                            selectedCompany.value = company;
                        }
                    }
                } else {
                    // Add new companies with timestamps
                    data.data.forEach((company) => addCompanyToCache(company));
                }
                companyResponse.value = data;
            } catch (error) {
                // The catalog renders an empty grid on failure, which reads as "no such
                // company" to anyone listening. Callers announce nothing while this is set.
                publicFetchFailed.value = true;
                showError('Error', (error instanceof Error && error.message) || 'Failed to fetch public companies');
            } finally {
                isLoading.value = false;
            }
        }

        function fetchCompany(id: number): Promise<void> {
            const inFlight = fetchesInFlight.get(id);
            if (inFlight) return inFlight;

            const fetching = loadCompany(id).finally(() => fetchesInFlight.delete(id));
            fetchesInFlight.set(id, fetching);
            return fetching;
        }

        async function loadCompany(id: number) {
            isLoading.value = true;

            try {
                const company = await companiesService.getCompany(id);
                selectedCompany.value = company;
                addCompanyToCache(company);
            } catch (error) {
                const code = error instanceof ApiError ? error.code : 'UNKNOWN';
                showError(
                    'Error',
                    (error instanceof Error && error.message) || `Something went wrong while fetching company. Code: GECOMP${code}`
                );
            } finally {
                isLoading.value = false;
            }
        }

        async function fetchCategoryStats() {
            // Return early if stats have already been fetched
            if (categoryStatsFetched.value) {
                return;
            }

            isLoading.value = true;
            // loadPublicStats never rejects: it answers with the committed snapshot when the
            // endpoint cannot, so the catalog keeps the exact counts it painted with rather
            // than the "All Data Brokers (0)" a failed fetch used to leave on a public page.
            const stats = await loadPublicStats(statsEndpoint);
            totalDataBrokers.value = stats.data_brokers;
            brokerCategories.value = stats.data_broker_types;
            categories.value = categoryLabels(stats.data_broker_types);
            categoryStatsFetched.value = true;
            isLoading.value = false;
        }

        return {
            selectedCompany,
            companies,
            filteredCompanies,
            searchQuery,
            fetchPublicCompanies,
            companyResponse,
            isLoading,
            publicFetchFailed,
            resetCompanies,
            categories,
            categoryKeys,
            fetchCategoryStats,
            selectedCategoryType,
            totalDataBrokers,
            fetchCompany,
            getCompanyById,
            brokerCategories,
        };
    }
);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useCompaniesStore, import.meta.hot));
}
